import { Button, Card, Dialog, DialogPanel, Select, SelectItem, TextInput } from "@tremor/react";
import { GitBranch, Pencil, Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Badge from "../components/Badge.jsx";
import {
  useRoutingRules,
  useCreateRoutingRule,
  useUpdateRoutingRule,
  useDeleteRoutingRule,
  usePreviewRoutingRules,
} from "../queries/routingRules.js";
import { useTeams } from "../queries/teams.js";
import { useServices } from "../queries/services.js";
import { useChannels } from "../queries/channels.js";
import { useMonitors } from "../queries/monitors.js";
import { fetchServers } from "../api/servers.js";

const CONDITION_FIELDS = ["source", "severity", "priority", "monitor_id", "server_id", "failure_reason", "tag"];
const ACTION_TYPES = ["set_team", "set_service", "set_severity", "set_priority", "add_tag", "notify_channel"];
const SEVERITIES = ["critical", "high", "medium", "low"];
const PRIORITIES = ["P1", "P2", "P3", "P4", "P5"];
// Mirrors core/incidents.py's _ORIGINS and derive_failure_reason() -- a value
// here that the backend doesn't actually produce would just silently never
// match, which is exactly the guesswork this picker exists to remove.
const SOURCES = ["automatic", "manual", "webhook", "server", "api"];
const FAILURE_REASONS = [
  "timeout",
  "connection_refused",
  "dns_failure",
  "ssl_error",
  "http_error",
  "ping_overdue",
  "unknown",
];
// Fields with a backend-fixed vocabulary -- rendered as a picker, never free text.
const ENUM_FIELDS = { severity: SEVERITIES, priority: PRIORITIES, source: SOURCES, failure_reason: FAILURE_REASONS };

// Picks the right input for a condition's value instead of one free-text box
// for every field -- guessing the exact literal (is it "high" or "High"? a
// UUID pasted from where?) is exactly how a rule silently never matches.
function ConditionValueInput({ field, op, value, onChange, monitors, servers, t }) {
  if (field === "tag") {
    return (
      <TextInput
        value={value ?? ""}
        onValueChange={onChange}
        placeholder={t("condition_value_placeholder")}
      />
    );
  }

  let options;
  if (ENUM_FIELDS[field]) {
    options = ENUM_FIELDS[field].map((v) => ({ value: v, label: v }));
  } else if (field === "monitor_id") {
    options = monitors.map((m) => ({ value: m.id, label: m.name }));
  } else if (field === "server_id") {
    options = servers.map((s) => ({ value: s.id, label: s.display_name || s.hostname }));
  } else {
    options = [];
  }

  if (op === "in") {
    const selected = Array.isArray(value) ? value : [];
    function toggle(v) {
      onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
    }
    return (
      <div className="flex flex-1 flex-wrap gap-1">
        {options.length === 0 && <p className="text-xs text-gray-400">{t("condition_no_options")}</p>}
        {options.map((o) => (
          <button
            type="button"
            key={o.value}
            onClick={() => toggle(o.value)}
            className={`rounded-full border px-2 py-0.5 text-xs ${
              selected.includes(o.value)
                ? "border-teal-600 bg-teal-50 text-teal-700"
                : "border-gray-300 text-gray-500 hover:border-gray-400"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <Select value={typeof value === "string" ? value : ""} onValueChange={onChange}>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </Select>
  );
}

function ConditionRow({ condition, onChange, onRemove, monitors, servers, t }) {
  const isTag = condition.field === "tag";
  const op = isTag ? "eq" : condition.op || "eq";

  function handleFieldChange(field) {
    // Reset value on field change -- a leftover string/array from the
    // previous field's shape won't mean anything for the new one.
    onChange({ field, op: "eq", value: "" });
  }

  function handleOpChange(newOp) {
    onChange({ ...condition, op: newOp, value: newOp === "in" ? [] : "" });
  }

  return (
    <div className="flex items-center gap-2">
      <Select value={condition.field} onValueChange={handleFieldChange}>
        {CONDITION_FIELDS.map((f) => (
          <SelectItem key={f} value={f}>{t(`condition_fields.${f}`)}</SelectItem>
        ))}
      </Select>
      {!isTag && (
        <Select value={op} onValueChange={handleOpChange}>
          <SelectItem value="eq">{t("op_eq")}</SelectItem>
          <SelectItem value="in">{t("op_in")}</SelectItem>
        </Select>
      )}
      <ConditionValueInput
        field={condition.field}
        op={op}
        value={condition.value}
        onChange={(v) => onChange({ ...condition, value: v })}
        monitors={monitors}
        servers={servers}
        t={t}
      />
      <button type="button" onClick={onRemove} className="shrink-0 text-gray-400 hover:text-red-500">
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function ActionRow({ action, onChange, onRemove, teams, services, channels, t }) {
  return (
    <div className="flex items-center gap-2">
      <Select value={action.type} onValueChange={(v) => onChange({ type: v })}>
        {ACTION_TYPES.map((a) => (
          <SelectItem key={a} value={a}>{t(`action_types.${a}`)}</SelectItem>
        ))}
      </Select>
      {action.type === "set_team" && (
        <Select value={action.team_id ?? ""} onValueChange={(v) => onChange({ ...action, team_id: v })}>
          {teams.map((tm) => <SelectItem key={tm.id} value={tm.id}>{tm.name}</SelectItem>)}
        </Select>
      )}
      {action.type === "set_service" && (
        <Select value={action.service_id ?? ""} onValueChange={(v) => onChange({ ...action, service_id: v })}>
          {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
        </Select>
      )}
      {action.type === "set_severity" && (
        <Select value={action.severity ?? ""} onValueChange={(v) => onChange({ ...action, severity: v })}>
          {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </Select>
      )}
      {action.type === "set_priority" && (
        <Select value={action.priority ?? ""} onValueChange={(v) => onChange({ ...action, priority: v })}>
          {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
        </Select>
      )}
      {action.type === "add_tag" && (
        <TextInput
          value={action.tag ?? ""}
          onValueChange={(v) => onChange({ ...action, tag: v })}
          placeholder={t("action_tag_placeholder")}
        />
      )}
      {action.type === "notify_channel" && (
        <Select value={action.channel_id ?? ""} onValueChange={(v) => onChange({ ...action, channel_id: v })}>
          {channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </Select>
      )}
      <button type="button" onClick={onRemove} className="shrink-0 text-gray-400 hover:text-red-500">
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function RuleEditor({ rule, teams, services, channels, onClose, onSaved }) {
  const { t } = useTranslation("routing_rules");
  const isEdit = !!rule;
  const [name, setName] = useState(rule?.name ?? "");
  const [position, setPosition] = useState(rule?.position ?? 0);
  const [isActive, setIsActive] = useState(rule?.is_active ?? true);
  const [conditions, setConditions] = useState(rule?.conditions ?? []);
  const [actions, setActions] = useState(rule?.actions ?? []);
  const createRule = useCreateRoutingRule();
  const updateRule = useUpdateRoutingRule();
  const saving = createRule.isPending || updateRule.isPending;

  // For the monitor_id/server_id condition pickers -- fetched here (not at
  // the page level) since the editor is the only place that needs them.
  const monitorsQuery = useMonitors({ limit: 100 });
  const monitors = monitorsQuery.data?.data ?? [];
  const [servers, setServers] = useState([]);
  useEffect(() => {
    fetchServers().then(setServers).catch(() => {});
  }, []);

  function updateCondition(i, next) {
    setConditions((prev) => prev.map((c, idx) => (idx === i ? next : c)));
  }
  function removeCondition(i) {
    setConditions((prev) => prev.filter((_, idx) => idx !== i));
  }
  function addCondition() {
    setConditions((prev) => [...prev, { field: "source", op: "eq", value: "" }]);
  }

  function updateAction(i, next) {
    setActions((prev) => prev.map((a, idx) => (idx === i ? next : a)));
  }
  function removeAction(i) {
    setActions((prev) => prev.filter((_, idx) => idx !== i));
  }
  function addAction() {
    setActions((prev) => [...prev, { type: "set_team" }]);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t("errors.name_required"));
      return;
    }
    const payload = { name: name.trim(), position: Number(position) || 0, is_active: isActive, conditions, actions };
    try {
      if (isEdit) {
        await updateRule.mutateAsync({ id: rule.id, data: payload });
        toast.success(t("success_save"));
      } else {
        await createRule.mutateAsync(payload);
        toast.success(t("success_create"));
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error ?? t("errors.save"));
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-2xl flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? t("editor.title_edit") : t("editor.title_new")}
          </h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">{t("editor.field_name")}</label>
                <TextInput required value={name} onValueChange={setName} placeholder={t("editor.field_name_placeholder")} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">{t("editor.field_position")}</label>
                <TextInput type="number" value={position} onValueChange={(v) => setPosition(v)} />
              </div>
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
              />
              {t("editor.field_active")}
            </label>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{t("editor.conditions_title")}</p>
                <Button type="button" size="xs" variant="secondary" icon={Plus} onClick={addCondition}>{t("editor.add_condition")}</Button>
              </div>
              <p className="mb-2 text-xs text-gray-400">{t("editor.conditions_hint")}</p>
              <div className="space-y-2">
                {conditions.map((c, i) => (
                  <ConditionRow
                    key={i}
                    condition={c}
                    onChange={(next) => updateCondition(i, next)}
                    onRemove={() => removeCondition(i)}
                    monitors={monitors}
                    servers={servers}
                    t={t}
                  />
                ))}
                {conditions.length === 0 && <p className="text-sm text-gray-400">{t("editor.no_conditions")}</p>}
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{t("editor.actions_title")}</p>
                <Button type="button" size="xs" variant="secondary" icon={Plus} onClick={addAction}>{t("editor.add_action")}</Button>
              </div>
              <div className="space-y-2">
                {actions.map((a, i) => (
                  <ActionRow
                    key={i}
                    action={a}
                    onChange={(next) => updateAction(i, next)}
                    onRemove={() => removeAction(i)}
                    teams={teams}
                    services={services}
                    channels={channels}
                    t={t}
                  />
                ))}
                {actions.length === 0 && <p className="text-sm text-gray-400">{t("editor.no_actions")}</p>}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <Button variant="secondary" type="button" onClick={onClose}>{t("cancel")}</Button>
            <Button type="submit" disabled={saving}>
              {saving ? t("saving") : isEdit ? t("editor.save") : t("editor.create")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

function PreviewPanel({ onClose, t }) {
  const preview = usePreviewRoutingRules();
  const [form, setForm] = useState({ source: "server", severity: "high", priority: "P2", monitor_id: "", server_id: "", failure_reason: "", tags: "" });

  async function handleRun() {
    try {
      await preview.mutateAsync({
        source: form.source,
        severity: form.severity,
        priority: form.priority,
        monitor_id: form.monitor_id || null,
        server_id: form.server_id || null,
        failure_reason: form.failure_reason || null,
        tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
    } catch {
      toast.error(t("errors.preview"));
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-md flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">{t("preview.title")}</h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <p className="text-sm text-gray-500">{t("preview.hint")}</p>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">{t("condition_fields.source")}</label>
            <TextInput value={form.source} onValueChange={(v) => setForm((p) => ({ ...p, source: v }))} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">{t("condition_fields.severity")}</label>
            <Select value={form.severity} onValueChange={(v) => setForm((p) => ({ ...p, severity: v }))}>
              {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">{t("condition_fields.priority")}</label>
            <Select value={form.priority} onValueChange={(v) => setForm((p) => ({ ...p, priority: v }))}>
              {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">{t("condition_fields.tag")} ({t("preview.comma_separated")})</label>
            <TextInput value={form.tags} onValueChange={(v) => setForm((p) => ({ ...p, tags: v }))} />
          </div>
          <Button onClick={handleRun} disabled={preview.isPending}>{t("preview.run")}</Button>

          {preview.data && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              {preview.data.matched_rule ? (
                <>
                  <p className="text-sm font-medium text-gray-900">{t("preview.matched", { name: preview.data.matched_rule.name })}</p>
                  <pre className="mt-2 overflow-x-auto text-xs text-gray-600">{JSON.stringify(preview.data.matched_rule.actions, null, 2)}</pre>
                </>
              ) : (
                <p className="text-sm text-gray-500">{t("preview.no_match")}</p>
              )}
            </div>
          )}
        </div>
      </DialogPanel>
    </Dialog>
  );
}

export default function RoutingRules() {
  const { t } = useTranslation("routing_rules");
  const { data, isLoading, isError } = useRoutingRules();
  const teamsQuery = useTeams();
  const servicesQuery = useServices();
  const channelsQuery = useChannels();
  const deleteRule = useDeleteRoutingRule();

  const rules = data ?? [];
  const teams = teamsQuery.data ?? [];
  const services = servicesQuery.data ?? [];
  const channels = channelsQuery.data ?? [];

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  function openCreate() {
    setEditing(null);
    setEditorOpen(true);
  }
  function openEdit(rule) {
    setEditing(rule);
    setEditorOpen(true);
  }

  async function handleDelete(rule) {
    if (!window.confirm(t("delete_confirm", { name: rule.name }))) return;
    try {
      await deleteRule.mutateAsync(rule.id);
      toast.success(t("success_delete"));
    } catch {
      toast.error(t("errors.delete"));
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setPreviewOpen(true)}>{t("preview_button")}</Button>
          <Button icon={Plus} onClick={openCreate}>{t("new")}</Button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">{t("loading")}</p>
      ) : isError ? (
        <p className="text-sm text-red-500">{t("errors.load")}</p>
      ) : rules.length === 0 ? (
        <Card className="px-5 py-12 text-center">
          <GitBranch className="mx-auto mb-3 h-8 w-8 text-gray-300" />
          <p className="text-sm font-medium text-gray-700">{t("empty.title")}</p>
          <p className="mt-1 text-sm text-gray-400">{t("empty.description")}</p>
          <Button className="mt-4" icon={Plus} onClick={openCreate}>{t("new")}</Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {rules.map((rule) => (
            <Card key={rule.id} className="p-0">
              <div className="flex items-center gap-4 px-5 py-4">
                <GitBranch className="h-8 w-8 shrink-0 rounded-lg bg-teal-50 p-1.5 text-teal-600" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-mono text-gray-400">#{rule.position}</span>
                    <p className="font-semibold text-gray-900">{rule.name}</p>
                    <Badge color={rule.is_active ? "emerald" : "gray"}>
                      {rule.is_active ? t("active") : t("inactive")}
                    </Badge>
                  </div>
                  <p className="mt-0.5 text-xs text-gray-400">
                    {t("conditions_count", { count: rule.conditions.length })} · {t("actions_count", { count: rule.actions.length })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="xs" variant="secondary" icon={Pencil} onClick={() => openEdit(rule)}>{t("edit")}</Button>
                  <Button size="xs" variant="secondary" color="red" icon={Trash2} onClick={() => handleDelete(rule)}>{t("delete")}</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editorOpen && (
        <RuleEditor
          rule={editing}
          teams={teams}
          services={services}
          channels={channels}
          onClose={() => setEditorOpen(false)}
          onSaved={() => setEditorOpen(false)}
        />
      )}

      {previewOpen && <PreviewPanel onClose={() => setPreviewOpen(false)} t={t} />}
    </div>
  );
}
